import { useState } from 'react';

const products = [
  {
    id: 'vpn',
    name: 'VPN',
    tagline: 'Private & Secure Browsing',
    description:
      'Our VPN application delivers military-grade encryption directly on your iPhone. Browse privately, protect your data on public Wi-Fi, and maintain full control over your digital footprint — all with a single tap.',
    features: ['AES-256 Encryption', 'Zero-Log Policy', 'Global Server Network', 'Kill Switch Protection'],
    image:
      'https://public.readdy.ai/ai/img_res/9c05de80c7e04760b9815cfe647ab79a.jpg',
    icon: 'ri-shield-keyhole-line',
  },
  {
    id: 'cleaner',
    name: 'Cleaner',
    tagline: 'Optimize Your iPhone',
    description:
      'Cleaner intelligently scans your device for junk files, duplicate photos, and unnecessary cache data. Reclaim storage space and keep your iPhone running at peak performance with smart, automated cleanup tools.',
    features: ['Duplicate Photo Finder', 'Cache Cleaner', 'Storage Analyzer', 'Smart Suggestions'],
    image:
      'https://readdy.ai/api/search-image?query=abstract%20clean%20minimal%20white%20and%20light%20gray%20geometric%20shapes%20floating%20particles%20soft%20gradient%20background%20representing%20cleanliness%20and%20optimization%20technology%20concept%20no%20text%20no%20people%20elegant%20composition&width=800&height=600&seq=cleaner01&orientation=landscape',
    icon: 'ri-sparkling-2-line',
  },
];

export default function ProductsSection() {
  const [active, setActive] = useState('vpn');
  const product = products.find((p) => p.id === active)!;

  return (
    <section id="products" className="py-28 px-8 md:px-20 bg-white">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-16">
          <p className="text-stone-400 text-sm uppercase tracking-widest mb-4">What We Build</p>
          <h2
            className="text-4xl md:text-5xl font-light text-stone-900"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Our Products
          </h2>
        </div>

        {/* Tab switcher */}
        <div className="inline-flex bg-stone-100 rounded-full p-1 mb-12">
          {products.map((p) => (
            <button
              key={p.id}
              onClick={() => setActive(p.id)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                active === p.id
                  ? 'bg-stone-900 text-white'
                  : 'text-stone-500 hover:text-stone-800'
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>

        {/* Product card */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-2xl overflow-hidden bg-stone-50 border border-stone-100">
          {/* Image */}
          <div className="relative h-72 lg:h-auto min-h-[320px]">
            <img
              src={product.image}
              alt={`${product.name} app`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent" />
            <div className="absolute top-6 left-6 w-12 h-12 flex items-center justify-center bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <i className={`${product.icon} text-white text-xl`} />
            </div>
          </div>

          {/* Content */}
          <div className="p-10 lg:p-14 flex flex-col justify-center">
            <p className="text-stone-400 text-xs uppercase tracking-widest mb-3">{product.tagline}</p>
            <h3
              className="text-3xl md:text-4xl font-light text-stone-900 mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {product.name}
            </h3>
            <p className="text-stone-600 text-base leading-relaxed mb-8">{product.description}</p>

            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {product.features.map((f) => (
                <li key={f} className="flex items-center gap-2 text-stone-700 text-sm">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-check-line text-stone-500" />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom tagline */}
        <div className="mt-16 flex flex-col sm:flex-row items-start sm:items-center gap-6 border-t border-stone-100 pt-12">
          <div className="flex-1">
            <p className="text-stone-900 font-medium text-lg mb-1">Built exclusively for iOS</p>
            <p className="text-stone-500 text-sm">
              All our products are native Swift applications, optimized for the latest iPhone hardware and iOS features.
            </p>
          </div>
          <div className="flex items-center gap-3 text-stone-400 text-sm">
            <i className="ri-apple-line text-xl" />
            <span>iOS Platform</span>
          </div>
        </div>
      </div>
    </section>
  );
}
